/**
 * 一括解析の 1 ジョブの上限 (functions の analyzeStreetViewBatch と管理画面で共有、docs/98 R-98 低で重複定義を解消)。
 * 当初 50 (forceReanalyze の MAX_POST_IDS と同値) だったが、登録済み 360度写真の一括解析 (docs/27 §7) は数百件に
 * なりうるため 200 に引き上げた (ワーカーは 1 点ずつ・2/min のため 200 点 ≒ 100 分)。
 */
export const STREET_SURVEY_BATCH_MAX_POINTS = 200;
/** 1 地点の AI 呼出回数: 従来の方式 (方向 4)。 */
export const STREET_SURVEY_LEGACY_CALLS_PER_POINT = 4;
/** 1 地点の AI 呼出回数: 新方式 (方向 4 + 選抜 1)。 */
export const STREET_SURVEY_V2_CALLS_PER_POINT = 5;
/** 既定の調査の key。 */
export const DEFAULT_STREET_SURVEY_KEY = 'default';
/** 投稿に調査ごとの最新結果を持つフィールド名。 */
export const STREET_SURVEY_RESULTS_FIELD = 'streetSurveyResults';
const SURVEY_KEY_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
/** 追加の調査の key として使えるか。 */
export function isValidStreetSurveyKey(key) {
    return key !== DEFAULT_STREET_SURVEY_KEY && key.length <= 40 && SURVEY_KEY_PATTERN.test(key);
}
/** 追加の調査 (壊れた定義は除く)。 */
export function listAdditionalStreetSurveys(districtAi) {
    const seen = new Set();
    const out = [];
    for (const s of districtAi?.streetSurveys ?? []) {
        if (!s || typeof s.key !== 'string' || !isValidStreetSurveyKey(s.key) || seen.has(s.key))
            continue;
        if (typeof s.settingsId !== 'string' || !s.settingsId)
            continue;
        seen.add(s.key);
        out.push(s);
    }
    return out;
}
/** 調査の一覧 (既定の調査が先頭)。 */
export function listStreetSurveyOptions(districtAi, defaultLabel) {
    return [
        { key: DEFAULT_STREET_SURVEY_KEY, label: defaultLabel, isDefault: true },
        ...listAdditionalStreetSurveys(districtAi).map((s) => ({
            key: s.key,
            label: s.label?.trim() || s.key,
            isDefault: false,
        })),
    ];
}
/** 既定の調査か (未指定・空も既定)。 */
export function isDefaultStreetSurvey(surveyKey) {
    return !surveyKey || surveyKey === DEFAULT_STREET_SURVEY_KEY;
}
/**
 * 調査の解析に使う地区の AI 解析設定の形。既定の調査は地区の設定そのもの、追加の調査は解析設定・テンプレート・
 * 机上調査の上書きだけを持つ設定 (地区の他の上書きは使わない)。見つからない調査は null。
 */
export function resolveStreetSurveyAiConfig(districtAi, surveyKey) {
    if (isDefaultStreetSurvey(surveyKey))
        return districtAi ?? null;
    const survey = listAdditionalStreetSurveys(districtAi).find((s) => s.key === surveyKey);
    if (!survey)
        return null;
    return {
        enabled: districtAi?.enabled !== false,
        settingsId: survey.settingsId,
        streetSurveyTemplateId: survey.streetSurveyTemplateId ?? null,
        ...(survey.streetSurveyOverride ? { streetSurveyOverride: survey.streetSurveyOverride } : {}),
    };
}
/** 投稿の追加の調査の結果 (無ければ null)。 */
export function readStreetSurveyResult(post, surveyKey) {
    const results = post?.[STREET_SURVEY_RESULTS_FIELD];
    if (!results || typeof results !== 'object' || Array.isArray(results))
        return null;
    const r = results[surveyKey];
    if (!r || typeof r !== 'object' || Array.isArray(r))
        return null;
    const summary = r;
    const aiAnalysis = summary.aiAnalysis && typeof summary.aiAnalysis === 'object' ? summary.aiAnalysis : null;
    if (!aiAnalysis)
        return null;
    return {
        aiAnalysis,
        pins: Array.isArray(summary.pins) ? summary.pins : [],
        extraction: summary.extraction ?? null,
        runId: typeof summary.runId === 'string' ? summary.runId : null,
        analyzedAt: typeof summary.analyzedAt === 'string' ? summary.analyzedAt : null,
    };
}
//# sourceMappingURL=streetSurveys.js.map